import React from "react"
import tw from "twin.macro"
import { Button, ButtonTheme } from "../formElements/button"
import { ReactComponent as FacebookIcon } from "../../assets/social/facebook.svg"
import { FacebookLogin } from "../facebook/login"
import { useTranslation } from "next-i18next"
import { useGetInstagramAccountStatsLazyQuery, useUpdateInstagramLinkedMutation } from "../../graphql/generated/graphql"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"
import { ReactComponent as InstagramIcon } from "../../assets/profile/instagram-black.svg"
import { DateTime } from "luxon"
import { useRouter } from "next/router"
import { Routes } from "../../routes"

interface InstagramIntegrationProps {
  showIntegrations: boolean
  validFacebookAccessToken: boolean
  isInstagramLinked: boolean
}
export const InstagramIntegration = ({
  showIntegrations,
  validFacebookAccessToken,
  isInstagramLinked,
}: InstagramIntegrationProps) => {
  const { t } = useTranslation("profile")
  const router = useRouter()
  const [updateInstagramLinked] = useUpdateInstagramLinkedMutation()
  const [getInstagramAccountStats, { data: instagramAccountStatsData }] = useGetInstagramAccountStatsLazyQuery()
  const statistic = instagramAccountStatsData?.InstagramAccountStats
  const showStatistic = !!statistic?.videoCrawlJobCount && !!statistic?.latestQuestionCreatedAt

  useEffectAsync(async () => {
    if (validFacebookAccessToken && isInstagramLinked) {
      await getInstagramAccountStats()
    }
  }, [validFacebookAccessToken, isInstagramLinked])

  return (
    <div tw="text-center bg-[#FCCAEB] rounded-md p-4 space-y-3" css={showIntegrations ? tw`block` : tw`hidden`}>
      <div tw="flex items-center justify-center text-[24px] font-bold">
        <InstagramIcon tw="mr-2" />
        {t("Integrations.instagram")}
      </div>
      <p
        css={"span {font-weight: bold}"}
        dangerouslySetInnerHTML={{
          __html: showStatistic
            ? t("Integrations.youtube_videos", {
                videoCrawlJobCount: statistic?.videoCrawlJobCount,
                latestQuestionCreatedAt: DateTime.fromISO(statistic?.latestQuestionCreatedAt).toFormat("MM/dd/yy"),
              })
            : t("Integrations.instagram_reels"),
        }}
      />
      {validFacebookAccessToken && isInstagramLinked && (
        <Button
          dataTestId="instagram-manage-settings"
          twStyle={tw`bg-[#ED47A8] disabled:(bg-[#f88dcb] hover:bg-[#f88dcb])`}
          theme={ButtonTheme.Secondary}
          action={t("Integrations.manage_btn")}
          onClickAsync={async () => await router.push({ pathname: Routes.FacebookSettings })}
        />
      )}
      {validFacebookAccessToken && !isInstagramLinked && (
        <Button
          dataTestId="instagram-manage-linked"
          twStyle={tw`w-[260px] bg-[#1877F2] rounded hover:bg-none disabled:bg-none`}
          action={t("Integrations.continue_with_facebook")}
          onClick={async () => {
            await updateInstagramLinked()
            await router.push({ pathname: Routes.FacebookSettings })
          }}
          icon={<FacebookIcon />}
          iconBefore={true}
        />
      )}
      {!validFacebookAccessToken && <FacebookLogin />}
    </div>
  )
}
